import { useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import type { CurrentUser, Venue } from '@/src/lib/types';
import type { DbUser, DbVenue } from '@/src/lib/db-types';
import { log } from '@/src/lib/logger';

export interface CurrentUserState {
  user: CurrentUser | null;
  venue: Venue | null;
  loading: boolean;
  error: string | null;
}

export function useCurrentUser(authUserId: string | null): CurrentUserState {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Settings saves fire this so names update everywhere without a page reload.
  useEffect(() => {
    const onUpdated = () => setReloadKey((k) => k + 1);
    window.addEventListener('opsalo:profile-updated', onUpdated);
    return () => window.removeEventListener('opsalo:profile-updated', onUpdated);
  }, []);

  useEffect(() => {
    if (!authUserId) {
      setUser(null);
      setVenue(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (reloadKey === 0) setLoading(true); // refreshes after edits stay silent
    setError(null);

    (async () => {
      const { data: uData, error: uErr } = await supabase
        .from('users')
        .select('id, auth_user_id, venue_id, name, email, phone, app_role, active, area, avatar_url, position')
        .eq('auth_user_id', authUserId)
        .maybeSingle<DbUser>();
      if (cancelled) return;
      if (uErr || !uData) {
        log.error('useCurrentUser: failed to load user row', { code: uErr?.code });
        setError(uErr?.message ?? 'No user row linked to this auth account.');
        setLoading(false);
        return;
      }

      const { data: vData, error: vErr } = await supabase
        .from('venues')
        .select('id, name, timezone, state')
        .eq('id', uData.venue_id)
        .is('deleted_at', null)
        .maybeSingle<DbVenue>();
      if (cancelled) return;
      if (vErr) {
        log.error('useCurrentUser: failed to load venue', { code: vErr.code });
        setError(vErr.message);
        setLoading(false);
        return;
      }
      if (!vData) {
        // Not a real error — the user's current venue is gone (soft-deleted)
        // and no fallback has kicked in for them yet. user stays set so the
        // shell can distinguish "no venue" from "not signed up at all," and
        // show its own screen for it instead of a generic error.
        setUser({
          id: uData.id,
          authUserId: uData.auth_user_id ?? authUserId,
          email: uData.email,
          name: uData.name,
          role: uData.app_role,
          area: uData.area,
          position: uData.position ?? null,
          venueId: uData.venue_id,
        });
        setVenue(null);
        setLoading(false);
        return;
      }

      setUser({
        id: uData.id,
        authUserId: uData.auth_user_id ?? authUserId,
        email: uData.email,
        name: uData.name,
        role: uData.app_role,
        area: uData.area,
        position: uData.position ?? null,
        venueId: uData.venue_id,
      });
      setVenue({
        id: vData.id,
        name: vData.name,
        ownerName: uData.name,
        timezone: vData.timezone || 'Australia/Sydney',
        state: (vData as { state?: string | null }).state ?? null,
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [authUserId, reloadKey]);

  return { user, venue, loading, error };
}